import { inject, Injectable, signal } from '@angular/core';
import { BackToBackGridsStateKey } from '@approot/models';
import { AuthService, UserStorageService } from '@approot/services';
import { clone } from '@approot/utilities';
import { isEqual } from 'lodash';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  BackToBackGrid,
  BackToBackTradeState,
  GetWhiteLabelRfqBlotterResponse,
  GetWhiteLabelRfqItem
} from './back-to-back-trades.types';
import { RunningState } from '@approot/positions-detail/shared/utils';
import { ColumnState } from 'ag-grid-community';

const DefaultBackToBackTradeState: BackToBackTradeState = {
  uuid: null,
  rows: [],
  runningState: RunningState.Idle,
  info: null
};

@Injectable({
  providedIn: 'root'
})
export class BackToBackService {
  displayBackToBackGrid() {
    throw new Error('Method not implemented.');
  }
  public stateChanged: Observable<BackToBackTradeState>;
  public isGridOpen = signal<boolean>(false);
  private state: BackToBackTradeState = DefaultBackToBackTradeState;
  private state$ = new ReplaySubject<BackToBackTradeState>(1);
  private userStorageService = inject(UserStorageService);
  private authService = inject(AuthService);

  constructor() {
    const savedGrid = this.userStorageService.getItem<BackToBackGrid>(
      BackToBackGridsStateKey
    );

    this.setState({
      ...DefaultBackToBackTradeState,
      grid: savedGrid
        ? clone(savedGrid)
        : {
            columnState: this.getPreviousBackToBackGridState()
          }
    });
    this.stateChanged = this.state$.asObservable();
  }

  public update(state: BackToBackTradeState): void {
    this.setState(state);
  }

  public displayBackToBackBlotter(): void {
    this.isGridOpen.update(_ => true);
  }

  public removeBackToBackGrid(): void {
    this.isGridOpen.update(_ => false);
  }

  public saveBackToBackGridState(columnState: ColumnState[]): void {
    const grid = this.state.grid;

    if (grid) {
      grid.columnState = columnState;
      this.update({ grid: clone(grid) });
      this.userStorageService.setItem(BackToBackGridsStateKey, grid);
    }
  }

  public getPreviousBackToBackGridState(): ColumnState[] {
    const grid = this.state.grid;
    return grid?.columnState || [];
  }

  public getWhiteLabelRfqBlotter(
    startDate: string,
    endDate: string
  ): Observable<GetWhiteLabelRfqItem[]> {
    const payload = {
      task: 'getWhiteLabelRfqBlotter',
      windowStart: startDate,
      windowEnd: endDate
    };
    return this.authService
      .fxPost<GetWhiteLabelRfqBlotterResponse>(
        payload,
        `whiteLabelServices`,
        `task=${payload.task}`
      )
      .pipe(map(resp => resp.response.items));
  }

  /****<PRIVATE>**************************************************************/

  private setState(state: BackToBackTradeState, emit = true): void {
    const newState = { ...this.state, ...state };

    if (Array.isArray(newState.grid)) {
      newState.grid = newState.grid.length > 0 ? newState.grid[0] : null;
    }

    if (isEqual(newState, this.state)) {
      return;
    }

    this.state = newState;
    if (emit) {
      this.state$.next(this.state);
    }
  }
}
